import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AutoPaySettingRow } from './autopay-setting-row';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { getPaymentMethodFromPaymentMethodOption } from '@/utils/payments';
import { MappedAutoPaySetting } from '@/hooks/payments/use-autopay-settings';
import { PaymentMethod } from '@/enums';
import { PaymentMethodBanner } from './payment-method-banner';
import { PaymentMethodOption } from '@/contexts/payments-context';
import { Platform, StyleProp, Text, View, ViewStyle } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { TYPOGRAPHY } from '@/styles';

const ACTION_BUTTON_HEIGHT = 54;
const ACTION_BUTTONS_PADDING_BOTTOM_MOBILE = 40;
const DISCLAIMER_BOTTOM_MARGIN = 32;

interface Props {
  autoPaySelfServeEnabled: boolean;
  availableAutoPaySettings: MappedAutoPaySetting[];
  containerStyle?: StyleProp<ViewStyle>;
  currencyCode: string | null;
  hasSavedAutoPaySetting: boolean;
  onChangePaymentMethod: () => void;
  onSaveAutoPaySetting: (autoPaySettingUuid: string) => void;
  onSelectAutoPaySetting: (autoPaySettingUuid: string) => void;
  savingAutoPaySetting: boolean;
  selectedAutoPaySettingUUID?: string;
  selectedPaymentMethod: PaymentMethodOption;
  shouldSaveAutoPaySetting: boolean;
}

export const AutoPaySettingsContainerMobile: React.FC<Props> = ({
  autoPaySelfServeEnabled,
  availableAutoPaySettings,
  containerStyle,
  hasSavedAutoPaySetting,
  onChangePaymentMethod,
  onSelectAutoPaySetting,
  onSaveAutoPaySetting,
  savingAutoPaySetting,
  selectedAutoPaySettingUUID,
  selectedPaymentMethod,
  shouldSaveAutoPaySetting,
}) => {
  const [styles] = useStyles();

  const paymentMethod: PaymentMethod = useMemo(() => {
    return getPaymentMethodFromPaymentMethodOption(selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  return (
    <View style={[styles.container, containerStyle]}>
      <PaymentMethodBanner
        onChangePaymentMethod={onChangePaymentMethod}
        paymentMethod={paymentMethod}
        selectedPaymentMethod={selectedPaymentMethod}
      />
      <View
        style={[
          styles.scrollViewContainer,
          {
            marginBottom:
              ACTION_BUTTONS_PADDING_BOTTOM_MOBILE +
              ACTION_BUTTON_HEIGHT +
              DISCLAIMER_BOTTOM_MARGIN,
          },
        ]}
      >
        <ScrollView showsVerticalScrollIndicator={false}>
          {availableAutoPaySettings.map(
            ({ frequency, description, uuid }, index) => (
              <AutoPaySettingRow
                description={description}
                disabled={!autoPaySelfServeEnabled}
                frequency={frequency}
                key={`autopay-setting-${uuid}`}
                onSelectAutoPaySetting={onSelectAutoPaySetting}
                rowStyle={[
                  index === availableAutoPaySettings.length - 1 &&
                    styles.autoPayOptionEndRow,
                ]}
                selected={uuid === selectedAutoPaySettingUUID}
                uuid={uuid}
              />
            ),
          )}
          {hasSavedAutoPaySetting ? (
            <View style={styles.contactDistributorDisclaimerWrapper}>
              <Text style={styles.contactDistributorDisclaimer}>
                {i18n.t('autopay.contact_distributor')}
              </Text>
            </View>
          ) : null}
        </ScrollView>
      </View>
      {autoPaySelfServeEnabled && selectedAutoPaySettingUUID ? (
        <View
          style={[
            styles.buttonWrapper,
            { paddingBottom: ACTION_BUTTONS_PADDING_BOTTOM_MOBILE },
          ]}
        >
          <Button
            disabled={!shouldSaveAutoPaySetting}
            loading={savingAutoPaySetting}
            onPress={() => {
              onSaveAutoPaySetting(selectedAutoPaySettingUUID);
            }}
            style={[
              styles.saveButton,
              Platform.OS !== 'web' ? styles.saveButtonMobile : undefined,
            ]}
            trackingData={null}
          >
            {i18n.t('save')}
          </Button>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  autoPayOptionEndRow: {
    borderBottomWidth: 0,
  },
  buttonWrapper: {
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    paddingHorizontal: 16,
    position: 'absolute',
    right: 0,
  },
  contactDistributorDisclaimer: {
    ...TYPOGRAPHY.small,
    color: theme.colors.primary,
    lineHeight: 21,
    textAlign: 'center',
  },
  contactDistributorDisclaimerWrapper: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    marginTop: 16,
    padding: 8,
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    rowGap: 8,
  },
  saveButton: {
    alignSelf: 'center',
  },
  saveButtonMobile: {
    paddingHorizontal: 24,
  },
  scrollViewContainer: {
    flex: 1,
  },
}));
